import { parseBoolean } from '@gitlab/frontend-utils';
import { convertObjectPropsToCamelCase } from '~/lib/utils/common_utils';
import { isObject } from '~/lib/utils/type_utility';
import * as Sentry from '~/sentry/sentry_browser_wrapper';

// One malformed value must not stop the whole repository app from mounting,
// so JSON values fall back instead of throwing. The backend always sends valid
// values, so a parse failure or wrong shape is a backend bug worth reporting.
const parseJSON = (value, { key, fallback, isValid }) => {
  if (!value) return fallback;

  try {
    const parsed = JSON.parse(value);
    if (!isValid(parsed)) throw new TypeError(`Unexpected ${key} in the repository app dataset`);
    return parsed;
  } catch (error) {
    Sentry.captureException(error, { extra: { key } });
    return fallback;
  }
};

const parseWebIdeButtonOptions = (value) =>
  convertObjectPropsToCamelCase(
    parseJSON(value, { key: 'webIdeButtonOptions', fallback: {}, isValid: isObject }),
  );

const parseDownloadLinks = (value) =>
  parseJSON(value, { key: 'downloadLinks', fallback: null, isValid: Array.isArray });

const parseCustomCodeDropdownClients = (value) =>
  parseJSON(value, { key: 'customCodeDropdownClients', fallback: [], isValid: Array.isArray }).map(
    (client) => convertObjectPropsToCamelCase(client),
  );

const parseForkInfo = (dataset, { projectPath, ref }) => {
  // Fork keys are omitted when the source is not visible, but ForkInfo still renders the
  // "inaccessible project" notice for those forks, so gate on `forked` instead.
  if (!parseBoolean(dataset.forked)) return null;

  return {
    canSyncBranch: parseBoolean(dataset.canSyncBranch),
    projectPath,
    selectedBranch: ref,
    sourceName: dataset.sourceName,
    sourcePath: dataset.sourcePath,
    sourceDefaultBranch: dataset.sourceDefaultBranch,
    aheadComparePath: dataset.aheadComparePath,
    behindComparePath: dataset.behindComparePath,
    createMrPath: dataset.createMrPath,
    viewMrPath: dataset.viewMrPath,
  };
};

// Parses the `#js-repository-app` dataset (TreeHelper#vue_repository_app_data)
// once into the root `props` and the single `provide`.
export const parseRepositoryAppDataset = (dataset) => {
  const {
    projectId,
    projectPath,
    projectShortPath,
    projectRootPath,
    fullName,
    resourceId,
    ref,
    escapedRef,
    refType,
    rootRef,
    suggestedTargetBranch,
    historyLink,
    canDownloadCode,
    hasRevsFile,
    explainCodeAvailable,
    comparePath,
    sshUrl,
    httpUrl,
    xcodeUrl,
    kerberosUrl,
    showNoSshKeyMessage,
    userSettingsSshKeysPath,
    newWorkspacePath,
    organizationId,
    isBinary,
    breadcrumbsCanCollaborate,
    breadcrumbsCanEditTree,
    breadcrumbsCanPushCode,
    breadcrumbsCanPushToBranch,
    breadcrumbsNewBranchPath,
    breadcrumbsNewTagPath,
    breadcrumbsNewBlobPath,
    breadcrumbsForkNewBlobPath,
    breadcrumbsForkNewDirectoryPath,
    breadcrumbsForkUploadBlobPath,
    breadcrumbsUploadPath,
    breadcrumbsNewDirPath,
  } = dataset;

  const {
    isFork,
    needsToFork,
    gitpodEnabled,
    isBlob,
    showEditButton,
    showWebIdeButton,
    showGitpodButton,
    showPipelineEditorButton,
    webIdeUrl,
    editUrl,
    pipelineEditorUrl,
    gitpodUrl,
    userPreferencesGitpodPath,
    userProfileEnableGitpodPath,
  } = parseWebIdeButtonOptions(dataset.webIdeButtonOptions);

  return {
    projectPath,
    projectShortPath,
    fullName,
    ref,
    escapedRef,
    props: {
      projectPath,
      projectId,
      currentRef: ref,
      refType,
      historyLink,
      forkInfo: parseForkInfo(dataset, { projectPath, ref }),
    },
    provide: {
      resourceId,
      originalBranch: ref,
      explainCodeAvailable: parseBoolean(explainCodeAvailable),
      canDownloadCode: parseBoolean(canDownloadCode),
      hasRevsFile: parseBoolean(hasRevsFile),
      canCollaborate: parseBoolean(breadcrumbsCanCollaborate),
      canEditTree: parseBoolean(breadcrumbsCanEditTree),
      canPushCode: parseBoolean(breadcrumbsCanPushCode),
      canPushToBranch: parseBoolean(breadcrumbsCanPushToBranch),
      // Generated target branch for new files and directories; nil when the user cannot edit.
      selectedBranch: suggestedTargetBranch,
      newBranchPath: breadcrumbsNewBranchPath,
      newTagPath: breadcrumbsNewTagPath,
      newBlobPath: breadcrumbsNewBlobPath,
      forkNewBlobPath: breadcrumbsForkNewBlobPath,
      forkNewDirectoryPath: breadcrumbsForkNewDirectoryPath,
      forkUploadBlobPath: breadcrumbsForkUploadBlobPath,
      uploadPath: breadcrumbsUploadPath,
      newDirPath: breadcrumbsNewDirPath,
      projectRootPath,
      comparePath,
      isReadmeView: false,
      isFork: parseBoolean(isFork),
      needsToFork: parseBoolean(needsToFork),
      isGitpodEnabledForUser: parseBoolean(gitpodEnabled),
      isBlob: parseBoolean(isBlob),
      showEditButton: parseBoolean(showEditButton),
      showWebIdeButton: parseBoolean(showWebIdeButton),
      isGitpodEnabledForInstance: parseBoolean(showGitpodButton),
      showPipelineEditorButton: parseBoolean(showPipelineEditorButton),
      webIdeUrl,
      editUrl,
      pipelineEditorUrl,
      gitpodUrl,
      userPreferencesGitpodPath,
      userProfileEnableGitpodPath,
      httpUrl,
      xcodeUrl,
      sshUrl,
      kerberosUrl,
      newWorkspacePath,
      downloadLinks: parseDownloadLinks(dataset.downloadLinks),
      showNoSshKeyMessage: parseBoolean(showNoSshKeyMessage),
      userSettingsSshKeysPath,
      isBinary: parseBoolean(isBinary),
      rootRef,
      organizationId,
      customCodeDropdownClients: parseCustomCodeDropdownClients(dataset.customCodeDropdownClients),
    },
  };
};
